// Centralised QA checklist configuration and helpers

import qaBriefAudio from "../../../data/qaBriefAudioChecklists.json";

// Category lists (brief + audio strings live in src/data/qaBriefAudioChecklists.json for one source of truth)
export const briefsChecklist = qaBriefAudio.briefs.map((text) => ({
  category: "briefs",
  text,
}));

export const audioChecklist = qaBriefAudio.audio.map((text) => ({
  category: "audio",
  text,
}));

export const designsChecklist = [
  { category: "designs", text: "Check if the designs match the brief" },
  { category: "designs", text: "Organization of artwork" },
  {
    category: "designs",
    text: "Design must meet plugin requirements (size, shape, colour and frame) (for QA to check the size/dimensions they will need a short course/workshop on how to navigate the AI files and check the sizes according to the plugin tag)",
  },
  {
    category: "designs",
    text: "The naming of designs must match the brief slides or brief naming (the design name could match the audio name used for the artwork) designers need to emphasize this, and the QA team needs to check it.",
  },
  {
    category: "designs",
    text: "If animation has audio, check if there is no audio inserted for it, audio must be removed from animations to prevent double audio generation (sometimes the audio needs to be aligned correctly to the longer videos, in this case, the audio is inserted in the video but the video should not play on loop)",
  },
];

export const designsHardcodedChecklist = [
  {
    category: "designs-hardcoded",
    text: "All hardcoded assets SHOULD have a layout in chronological order. Is there a proper artboard with the appropriate scaling?",
  },
  {
    category: "designs-hardcoded",
    text: "Are all the buttons/arrows/icons created?",
  },
  {
    category: "designs-hardcoded",
    text: "Has the artwork needed (images and gifs) been exported? This includes textboxes, placeholders, and outlines/boxes as shown in the brief.",
  },
  {
    category: "designs-hardcoded",
    text: "If it is interactive: Are the well-done/incorrect clipboards exported?",
  },
  {
    category: "designs-hardcoded",
    text: "Has the green tick icon and red cross been exported?",
  },
  {
    category: "designs-hardcoded",
    text: "If there is a pop-up, have the exit buttons/arrows been exported?",
  },
  {
    category: "designs-hardcoded",
    text: "If it is an experiment, have all the elements needed been exported? Has the correct background/layout been used?",
  },
  {
    category: "designs-hardcoded",
    text: "If an action needs to be animated within the code, have all the frames been exported to execute the action seamlessly?",
  },
  {
    category: "designs-hardcoded",
    text: "All notes pages included in the brief SHOULD be designed. Has it been exported?",
  },
];

export const pluginsChecklist = [
  { category: "plugins", text: "Heading (always in all caps and in bold)" },
  {
    category: "plugins",
    text: "Intro or instruction text (always sentence case, instruction needs to be clear on what the user needs to do e.g. Press next/answer to learn more about xyz… OR Click on each icon/tab/number/image to learn more about xyz…)",
  },
  {
    category: "plugins",
    text: "What type of plugin is supposed to be generated (Does the teamwork tag match the brief uploaded? A common confusion is the round layout plugin vs scene icon plugin tags. These plugin tags are sometimes used interchangeably. Another example is the correct review plugin being used, there is the 'review' tag and the 'block review' tag, these are 2 different plugin types).",
  },
  {
    category: "plugins",
    text: "Availability of all audios indicated in the brief.",
  },
  {
    category: "plugins",
    text: "Check if the names of the audio correlate with the brief audio script codes.",
  },
  {
    category: "plugins",
    text: "Availability of all the designs indicated in the brief.",
  },
  { category: "plugins", text: "Check if the designs correlate with the brief." },
  {
    category: "plugins",
    text: "Should a notes page be included or not? Can the plugin generate the notes or is a notes page needed?",
  },
  {
    category: "plugins",
    text: "Apply corrections mentioned in TW, especially the notes page",
  },
  {
    category: "plugins",
    text: "Does the plugin correlate with the brief, audio and designs?",
  },
  {
    category: "plugins",
    text: "Should the pen icon be included or not in the instruction text line?",
  },
  {
    category: "plugins",
    text: "Is there an intro image/video included? Is there a start page?",
  },
  {
    category: "plugins",
    text: "Is the correct plugin theme used relevant to the subject e.g. English uses the blue theme.?",
  },
  {
    category: "plugins",
    text: "Is the correct loading and complete screen used (dependent on subject/lower grade assets)?",
  },
  {
    category: "plugins",
    text: "Is the plugin saved in the correct folder (export folder/final export/plugin folder)?",
  },
  {
    category: "plugins",
    text: "Has the updated spreadsheet been uploaded?",
  },
  {
    category: "plugins",
    text: "Have you included the pathway on teamwork, especially for new briefs?",
  },
];

export const hardcodedAssetsChecklist = [
  { category: "hardcoded", text: "Is there a loading screen?" },
  {
    category: "hardcoded",
    text: "Is the heading and subheading text according to the brief?",
  },
  {
    category: "hardcoded",
    text: "Does the correct button/icon show for the correct screen?",
  },
  {
    category: "hardcoded",
    text: "Do all the functional buttons work as intended? (speaker icon, info icon, pop-up icons, zoom icons etc.)",
  },
  {
    category: "hardcoded",
    text: "Is the correct audio playing for the artwork it is supposed to show?",
  },
  {
    category: "hardcoded",
    text: "If it is an activity for the feedback: Is it 2 submissions then the correct answer is shown?",
  },
  {
    category: "hardcoded",
    text: "Is it immediate feedback?",
  },
  {
    category: "hardcoded",
    text: "Are the correct answers marked correctly? If it is a drag-and-drop, can some answers be dragged to multiple places and still be marked correct if it is still on the correct side? E.g. X + Y + Z = Z + X + Y",
  },
  {
    category: "hardcoded",
    text: "Does the correct ping sound play when the answer is correct/incorrect? Should the well-done/incorrect clipboard show?",
  },
  {
    category: "hardcoded",
    text: "Does the clicking sound play when something is clicked?",
  },
  {
    category: "hardcoded",
    text: "Is there a copy of the diagram/notes/table screen?",
  },
  {
    category: "hardcoded",
    text: "Is there a loading and complete screen? Does the complete screen jingle sound play?",
  },
];

// Categories keyed by id for reuse
export const CHECKLIST_BY_CATEGORY = {
  briefs: briefsChecklist,
  audio: audioChecklist,
  designs: designsChecklist,
  "designs-hardcoded": designsHardcodedChecklist,
  plugins: pluginsChecklist,
  hardcoded: hardcodedAssetsChecklist,
};

// Which categories apply to which asset type
export const APPLICABLE_CATEGORIES_BY_TYPE = {
  plugin: ["briefs", "audio", "designs", "plugins"],
  hardcoded: ["briefs", "audio", "designs-hardcoded", "hardcoded"],
};

export const getApplicableCategories = (assetType = "plugin") => {
  return (
    APPLICABLE_CATEGORIES_BY_TYPE[assetType] ||
    APPLICABLE_CATEGORIES_BY_TYPE.plugin
  );
};

export const getTotalChecklistItemsForType = (assetType = "plugin") => {
  const applicable = getApplicableCategories(assetType);
  return applicable.reduce((sum, category) => {
    const items = CHECKLIST_BY_CATEGORY[category] || [];
    return sum + items.length;
  }, 0);
};


